// app/src/features/layout/useLayoutShell.js
// The rail panel's width (`useRailPanel`) is a separate hook because it reads the timeline
// geometry's refs, which exist only after `useTimelineGeometry` runs.
import { useDrawerStack } from "../../hooks/useDrawerStack.js";
import { useForecastSearch } from "../../hooks/useForecastSearch.js";
import { useMeasuredHeight } from "../../hooks/useMeasuredHeight.js";
import { useNavRailPanelWidth, useNavRailSections } from "../../hooks/useNavRail.js";
import { useSettledFlag } from "../../hooks/useSettledFlag.js";
import { useTrackRecord } from "../../hooks/useTrackRecord.js";

/**
 * The rail's sections, the Forecasts panel search, the Track record flag, the drawer z-order and
 * the forecast header's measured height.
 *
 * @param {object} deps
 * @param {boolean} deps.contractPopupOpen  the Question panel's own state (AppInner's)
 * @param {Function} deps.setContractPopupOpen
 * @param {boolean} deps.tsDrawerOpen  the Signal Library drawer (useTsdDrawer)
 */
export function useLayoutShell({ contractPopupOpen, setContractPopupOpen, tsDrawerOpen }) {
  const trackRecord = useTrackRecord();
  // The navigation rail: a permanent icon rail on the left with ONE panel beside it. The
  // Forecasts and Chat panels are derived from its one active section, so they are never open
  // together.
  const {
    logOpen,
    chatOpen,
    contractOpen,
    setLogOpen,
    setChatOpen,
    setContractOpen,
    activeSection: railSection,
    switchedSection: railSwitched,
    selectSection: selectRailSection,
    toggleCollapse: toggleRailCollapse,
  } = useNavRailSections({
    contractOpen: contractPopupOpen,
    setContractOpen: setContractPopupOpen,
  });
  // The Forecasts panel's title search.
  const forecastSearch = useForecastSearch({ panelOpen: logOpen });
  // Side-drawer stacking: the most recently opened of the four renders on top, except that the
  // open Signal Library stays above the rail panels.
  const drawerZ = useDrawerStack({
    log: logOpen,
    chat: chatOpen,
    tsd: tsDrawerOpen,
    contract: contractOpen,
  });
  // The forecast header (ForecastHeader) sits above the lane inside the timeline column: the
  // sticky line labels (TODAY / GENERATED / FORECAST) start just under it, and nothing floats
  // over the lines any more, so they need no fade.
  const { ref: forecastHeaderRef, height: forecastHeaderH } = useMeasuredHeight();
  const railTimelineDrop = 0;
  const timelineLabelTop = forecastHeaderH;
  const railLineFade = null;
  return {
    trackRecord,
    logOpen,
    chatOpen,
    contractOpen,
    setLogOpen,
    setChatOpen,
    setContractOpen,
    railSection,
    railSwitched,
    selectRailSection,
    toggleRailCollapse,
    forecastSearch,
    drawerZ,
    forecastHeaderRef,
    forecastHeaderH,
    railTimelineDrop,
    timelineLabelTop,
    railLineFade,
  };
}

/**
 * The rail panel's width and its settled flag. Called after `useTimelineGeometry`: the drag pins
 * the day at the panel's edge through the geometry's `vlRef` / `ppdRef`.
 *
 * @param {object} deps
 * @param {boolean} deps.forecastTracking
 * @param {string|null} deps.railSection  the showing section (useLayoutShell), null when collapsed
 * @param {{current: number}} deps.vlRef
 * @param {{current: number}} deps.ppdRef
 * @param {() => void} deps.scheduleRender
 * @param {number} deps.uiZoom
 */
export function useRailPanel({
  forecastTracking,
  railSection,
  vlRef,
  ppdRef,
  scheduleRender,
  uiZoom,
}) {
  // The rail panel's width, shared by both sections: resizing one resizes the other.
  const {
    panelWidth: railPanelWidth,
    reframeRef: railReframeRef,
    handleProps: railResizeHandle,
  } = useNavRailPanelWidth({
    // The Forecast Manager's floor, the larger of the two, so neither section is squeezed.
    minWidth: forecastTracking ? 330 : 272,
    vlRef,
    ppdRef,
    scheduleRender,
    uiZoom,
  });
  const railPanelOpen = !!railSection;
  // True once the opening slide (--rail-slide, 260ms) has covered the lane's left edge.
  const railPanelSettled = useSettledFlag(railPanelOpen, 260);
  return { railPanelWidth, railReframeRef, railResizeHandle, railPanelOpen, railPanelSettled };
}
